import { create } from "zustand"
import {
  getStatusSnapshot,
  getMetricsHistory,
  getFeed,
  getAutopilotStatus,
  getAuthorityStatus,
  getSlos,
  getDiagnosis,
  generateShiftReport,
  toggleAutopilot,
  vetoAction,
  acknowledgeAlert,
  resolveAlert,
  type StatusSnapshot,
  type MetricsPoint,
  type FeedEntry,
  type AutopilotStatus,
  type AuthorityStatus,
  type SloData,
} from "@/lib/api"

// ── Topology node model ──────────────────────────────────────────────────

export type NodeHealth = "healthy" | "degraded" | "critical" | "unknown"

export interface TopoNode {
  id: string
  label: string
  type: "api" | "queue" | "worker" | "gpu" | "database" | "storage" | "users"
  health: NodeHealth
  metric: string
  metricValue: string | number
  detail: Record<string, string | number | boolean | null>
}

export interface TopoEdge {
  id: string
  source: string
  target: string
  throughput: number // relative 0-1
  health: NodeHealth
  label?: string
}

// ── Diagnosis model ──────────────────────────────────────────────────────

export interface DiagnosisResult {
  severity: string
  analysis: string
  likely_issue?: string
  explanation?: string
  suggested_fix?: string
  confidence?: number
  actions: Array<{
    label: string
    detail?: string
    auto_fixable?: boolean
    action_key?: string
  }>
}

// ── Store ────────────────────────────────────────────────────────────────

interface SystemState {
  // Data
  snapshot: StatusSnapshot | null
  metrics: MetricsPoint[]
  feed: FeedEntry[]
  autopilot: AutopilotStatus | null
  authority: AuthorityStatus | null
  slos: SloData | null
  diagnosis: DiagnosisResult | null
  report: any

  // Topology (derived from snapshot)
  topoNodes: TopoNode[]
  topoEdges: TopoEdge[]
  selectedNodeId: string | null

  // UI state
  loading: boolean
  diagnosing: boolean
  generating: boolean

  // Actions — data loading
  loadSnapshot: () => Promise<void>
  loadFeed: () => Promise<void>
  loadMetrics: () => Promise<void>
  loadAutopilot: () => Promise<void>
  loadSlos: () => Promise<void>
  loadAll: () => Promise<void>

  // Actions — mutations
  runDiagnosis: () => Promise<void>
  runReport: () => Promise<void>
  doToggleAutopilot: () => Promise<void>
  doVeto: (actionId: string) => Promise<void>
  doAckAlert: (alertId: number) => Promise<void>
  doResolveAlert: (alertId: number) => Promise<void>

  // Actions — UI
  selectNode: (nodeId: string | null) => void
  dismissDiagnosis: () => void
  dismissReport: () => void
}

// ── Derive topology from snapshot ────────────────────────────────────────

function deriveTopology(s: StatusSnapshot | null): { nodes: TopoNode[]; edges: TopoEdge[] } {
  if (!s) return { nodes: [], edges: [] }

  const queueHealth: NodeHealth =
    s.queue_depth > 50 ? "critical" : s.queue_depth > 20 ? "degraded" : "healthy"
  const gpuHealth: NodeHealth =
    (s.gpu_pct ?? 0) > 95 ? "critical" : (s.gpu_pct ?? 0) > 80 ? "degraded" : "healthy"
  const stalledJobs = (s as any).stalled_jobs ?? 0
  const slowJobs = (s as any).slow_jobs ?? 0
  const longestActiveS = (s as any).longest_active_s ?? 0
  const workerHealth: NodeHealth =
    !s.worker_alive ? "critical" : stalledJobs > 0 ? "critical" : s.gpu_workers_ready === 0 ? "degraded" : slowJobs > 0 ? "degraded" : "healthy"
  const apiHealth: NodeHealth =
    s.alerts_critical > 0 ? "critical" : s.alerts_warn > 0 ? "degraded" : "healthy"
  const dbHealth: NodeHealth =
    (s.disk_pct ?? 0) > 90 ? "critical" : (s.disk_pct ?? 0) > 80 ? "degraded" : "healthy"

  const nodes: TopoNode[] = [
    {
      id: "users",
      label: "Users",
      type: "users",
      health: "healthy",
      metric: "Active",
      metricValue: s.active_jobs,
      detail: { total_cloaks: s.total_cloaks },
    },
    {
      id: "api",
      label: "API",
      type: "api",
      health: apiHealth,
      metric: "Jobs/min",
      metricValue: s.throughput_jobs_per_min,
      detail: {
        alerts_critical: s.alerts_critical,
        alerts_warn: s.alerts_warn,
        alerts_total: s.alerts_total,
        throughput_avg_s: s.throughput_avg_s,
      },
    },
    {
      id: "redis",
      label: "Redis Queue",
      type: "queue",
      health: queueHealth,
      metric: "Depth",
      metricValue: s.queue_depth,
      detail: {
        queue_pub: s.queue_pub,
        queue_pri: s.queue_pri,
        queue_default: s.queue_default,
      },
    },
    {
      id: "worker",
      label: "Workers",
      type: "worker",
      health: workerHealth,
      metric: "Ready",
      metricValue: `${s.gpu_workers_ready}/${s.gpu_workers_alive}`,
      detail: {
        alive: s.gpu_workers_alive,
        ready: s.gpu_workers_ready,
        worker_alive: s.worker_alive,
        engine_ready: s.engine_ready,
        stalled_jobs: stalledJobs,
        slow_jobs: slowJobs,
        longest_active_s: longestActiveS > 0 ? `${(longestActiveS / 60).toFixed(1)}min` : null,
      },
    },
    {
      id: "gpu",
      label: "GPU",
      type: "gpu",
      health: gpuHealth,
      metric: "Util",
      metricValue: s.gpu_pct != null ? `${s.gpu_pct}%` : "—",
      detail: {
        gpu_pct: s.gpu_pct,
        gpu_peak: s.gpu_peak,
        gpu_temp: s.gpu_temp,
      },
    },
    {
      id: "postgres",
      label: "Postgres",
      type: "database",
      health: dbHealth,
      metric: "Disk",
      metricValue: s.disk_pct != null ? `${Math.round(s.disk_pct)}%` : "—",
      detail: { disk_pct: s.disk_pct },
    },
    {
      id: "storage",
      label: "Object Storage",
      type: "storage",
      health: "healthy",
      metric: "Protected",
      metricValue: s.total_cloaks?.toLocaleString() ?? "—",
      detail: { total_cloaks: s.total_cloaks },
    },
  ]

  // Throughput is normalized 0-1 relative to 20 jobs/min as "full"
  const tNorm = Math.min(1, s.throughput_jobs_per_min / 20)
  const qNorm = Math.min(1, s.queue_depth / 50)

  const edges: TopoEdge[] = [
    {
      id: "users-api",
      source: "users",
      target: "api",
      throughput: tNorm,
      health: apiHealth,
      label: `${s.throughput_jobs_per_min}/min`,
    },
    {
      id: "api-redis",
      source: "api",
      target: "redis",
      throughput: tNorm,
      health: queueHealth,
      label: `${s.queue_depth} queued`,
    },
    {
      id: "redis-worker",
      source: "redis",
      target: "worker",
      throughput: qNorm,
      health: workerHealth,
    },
    {
      id: "worker-gpu",
      source: "worker",
      target: "gpu",
      throughput: tNorm,
      health: gpuHealth,
    },
    {
      id: "worker-storage",
      source: "worker",
      target: "storage",
      throughput: tNorm,
      health: "healthy",
    },
    {
      id: "api-postgres",
      source: "api",
      target: "postgres",
      throughput: 0.3,
      health: dbHealth,
    },
  ]

  return { nodes, edges }
}

// ── Create store ─────────────────────────────────────────────────────────

export const useSystemStore = create<SystemState>((set, get) => ({
  snapshot: null,
  metrics: [],
  feed: [],
  autopilot: null,
  authority: null,
  slos: null,
  diagnosis: null,
  report: null,
  topoNodes: [],
  topoEdges: [],
  selectedNodeId: null,
  loading: true,
  diagnosing: false,
  generating: false,

  loadSnapshot: async () => {
    try {
      const data = await getStatusSnapshot()
      const { nodes, edges } = deriveTopology(data)
      set({ snapshot: data, topoNodes: nodes, topoEdges: edges })
    } catch {}
  },

  loadFeed: async () => {
    try {
      const data = await getFeed(50)
      set({ feed: data.entries })
    } catch {}
  },

  loadMetrics: async () => {
    try {
      const data = await getMetricsHistory(60)
      set({ metrics: data.points })
    } catch {}
  },

  loadAutopilot: async () => {
    try {
      const [ap, auth] = await Promise.all([getAutopilotStatus(), getAuthorityStatus()])
      set({ autopilot: ap, authority: auth })
    } catch {}
  },

  loadSlos: async () => {
    try {
      const data = await getSlos()
      set({ slos: data })
    } catch {}
  },

  loadAll: async () => {
    const { loadSnapshot, loadFeed, loadMetrics, loadAutopilot, loadSlos } = get()
    await Promise.all([loadSnapshot(), loadFeed(), loadMetrics(), loadAutopilot(), loadSlos()])
    set({ loading: false })
  },

  runDiagnosis: async () => {
    set({ diagnosing: true })
    try {
      const result = await getDiagnosis()
      set({ diagnosis: result as DiagnosisResult })
    } catch (e) {
      console.error("Diagnosis error:", e)
    } finally {
      set({ diagnosing: false })
    }
  },

  runReport: async () => {
    set({ generating: true })
    try {
      const result = await generateShiftReport()
      set({ report: result })
    } catch (e) {
      console.error("Report error:", e)
    } finally {
      set({ generating: false })
    }
  },

  doToggleAutopilot: async () => {
    const { autopilot } = get()
    if (!autopilot) return
    const next = !autopilot.enabled
    if (!next && !confirm("Disable autopilot? Automated actions will stop.")) return
    try {
      const result = await toggleAutopilot(next)
      set({ autopilot: result })
    } catch (e) {
      console.error("Autopilot toggle error:", e)
    }
  },

  doVeto: async (actionId: string) => {
    try {
      await vetoAction(actionId)
      await get().loadAutopilot()
    } catch (e) {
      console.error("Veto error:", e)
    }
  },

  doAckAlert: async (alertId: number) => {
    try {
      await acknowledgeAlert(alertId)
      await get().loadFeed()
    } catch (e) {
      console.error("Ack error:", e)
    }
  },

  doResolveAlert: async (alertId: number) => {
    try {
      await resolveAlert(alertId)
      await Promise.all([get().loadFeed(), get().loadSnapshot()])
    } catch (e) {
      console.error("Resolve error:", e)
    }
  },

  selectNode: (nodeId) => set({ selectedNodeId: nodeId }),
  dismissDiagnosis: () => set({ diagnosis: null }),
  dismissReport: () => set({ report: null }),
}))
